import { db } from "./firebase"
import firebase from "firebase"
import moment from "moment"

/**
 * Stableford loyalty points - the single source of truth for how a client's
 * balance is worked out.
 *
 *   earned  - every booking of theirs that has started travelling (check_in on
 *             or before today) and is fully paid (balance_outstanding <= 0)
 *             contributes floor(stableford.amount) points.
 *   spent   - every non-voided `SPENT` entry in clients/{id}/points_ledger.
 *   balance - earned minus spent.
 *
 * The same calculation is mirrored server-side in `functions-points/src/balance.ts`
 * so a third-party redemption partner is always looking at the same number as
 * staff are.
 */

/** 10 Stableford points = £1.00 off a future booking. */
export const POINTS_PER_POUND = 10

/**
 * Cash value of a points figure, in GBP.
 *
 * @param {number} points
 * @returns {number}
 */
export const pointsToGBP = (points) => {
	return Number((Number(points || 0) / POINTS_PER_POUND).toFixed(2))
}

/**
 * Format a points figure as its GBP value.
 *
 * @param {number} points
 * @returns {string}
 */
export const formatPointsValue = (points) => {
	return new Intl.NumberFormat("en-GB", {
		style: "currency",
		currency: "GBP",
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(pointsToGBP(points))
}

/**
 * Points earned across a client's travelled, fully-paid bookings.
 *
 * @param {string} clientID
 * @returns {Promise<number>}
 */
export const fetchPointsEarned = async (clientID) => {
	const startOfToday = firebase.firestore.Timestamp.fromMillis(moment().startOf("day").valueOf())

	const bookings = await db.collection("bookings").where("client", "==", clientID).where("check_in", "<=", startOfToday).get()

	return bookings.docs.reduce((total, doc) => {
		const { balance_outstanding, stableford } = doc.data() || {}

		// Only fully-paid bookings count towards the balance.
		if (Number(balance_outstanding || 0) > 0) return total

		return total + Math.floor(Number(stableford?.amount) || 0)
	}, 0)
}

/**
 * Points already spent from a client's ledger. Voided entries (a partner
 * redemption reversed by staff) are skipped so the points return to the client.
 *
 * @param {string} clientID
 * @returns {Promise<number>}
 */
export const fetchPointsSpent = async (clientID) => {
	const ledger = await db.collection(`clients/${clientID}/points_ledger`).where("type", "==", "SPENT").get()

	return ledger.docs.reduce((total, doc) => {
		const entry = doc.data() || {}

		if (entry.voided === true) return total

		return total + (Number(entry.points) || 0)
	}, 0)
}

/**
 * Full balance breakdown for a client.
 *
 * @param {string} clientID
 * @returns {Promise<{ earned: number, spent: number, balance: number, valueGBP: number }>}
 */
export const fetchStablefordBalance = async (clientID) => {
	const earned = await fetchPointsEarned(clientID)
	const spent = await fetchPointsSpent(clientID)
	const balance = earned - spent

	return {
		earned,
		spent,
		balance,
		valueGBP: pointsToGBP(balance),
	}
}
